import React from 'react';
import { useStyles } from 'uinix-ui';

import { ActionMode } from '../../../types/ActionMode';
import { IconType } from '../../../types/IconType';
import { Nullable } from '../../../types/Nullable';
import { Option } from '../../../types/Option';
import { SharedChipProps } from '../../../types/SharedChipProps';
import { Box } from '../../Box/Box';
import { Chips } from '../../Chips/Chips';
import { Icon } from '../../Icon/Icon';
import { Layout } from '../../Layout/Layout';
import { Link } from '../../Link/Link';
import { MarkedText } from '../../MarkedText/MarkedText';
import { CenterTruncateText } from '../../Text/subcomponents/CenterTruncateText';
import { TruncateText } from '../../Text/subcomponents/TruncateText';
import { Tooltip } from '../../Tooltip/Tooltip';

const iconMarginTopOffset = '3px'; // this is a hack but flex center alignments do not work due to the complex layout needs with non-interoperable icon and text line-heights, so this value is explicitly used to express the desire to center a small icon against a body text.
const iconMarginXOffset = -1;
const iconSpacing = 1;

interface Props<V = unknown> {
  option: Option<V>;
  enableIndicator?: boolean;
  pathname?: string;
  isFocused?: boolean;
  isIndeterminate?: boolean;
  isMulti?: boolean;
  isSelected?: boolean;
  search?: string;
  onClick?: (value: V) => void;
  onHover?: (option: Option) => void;
  onMouseDown?: React.MouseEventHandler<HTMLDivElement>;
}

export const MenuOption = ({
  option,
  enableIndicator,
  pathname,
  isFocused,
  isIndeterminate,
  isMulti,
  isSelected,
  search,
  onClick,
  onHover,
  onMouseDown,
}: Props) => {
  const {
    chips,
    disabled,
    href,
    disabledReason,
    isDanger,
    label,
    leftContent,
    leftIcon,
    leftIconTooltip,
    mode,
    rightIcon,
    rightIconTooltip,
    description,
    descriptionTruncateMode,
    tags,
    tooltip,
  } = option;
  const styles = useStyles();

  const handleClick = () => onClick?.(option.value);

  const handleHover = () => onHover?.(option);
  let status: 'active' | 'info' | undefined = undefined;
  if (option.isActive || isSelected) {
    status = 'active';
  } else if (isFocused) {
    status = 'info';
  }

  const menuContent = (
    <Tooltip
      tooltip={tooltip || disabledReason}
      triggerStyle={componentStyles.tooltipTrigger}
    >
      <Layout
        align="center"
        disabled={!disabledReason && disabled}
        aria-disabled={!!(disabled || disabledReason)}
        justify="space-between"
        spacing={2}
        styles={[
          componentStyles.base,
          styles.colors.status,
          disabled || disabledReason ? styles.disabledVisual : null,
        ]}
        styleProps={{
          status: status,
        }}
        onClick={handleClick}
        onMouseDown={onMouseDown}
        onMouseEnter={handleHover}
        role="option"
        aria-selected={isSelected}
      >
        <LeftContent
          enableIndicator={enableIndicator}
          isDanger={isDanger}
          isIndeterminate={isIndeterminate}
          isMulti={isMulti}
          isSelected={isSelected}
          label={label}
          leftIcon={leftIcon}
          mode={mode}
          overrideContent={leftContent}
          search={search}
          description={description}
          descriptionTruncateMode={descriptionTruncateMode}
          tags={tags}
          tooltip={leftIconTooltip}
        />
        <RightContent
          chips={chips}
          rightIcon={rightIcon}
          tooltip={rightIconTooltip}
        />
      </Layout>
    </Tooltip>
  );

  const isLink = !disabled && (Boolean(pathname) || Boolean(href));

  return (
    <Box>
      {isLink ? (
        <Link href={href} pathname={pathname}>
          {menuContent}
        </Link>
      ) : (
        menuContent
      )}
    </Box>
  );
};

interface LeftContentProps {
  label: string;
  enableIndicator?: boolean;
  isDanger?: boolean;
  isIndeterminate?: boolean;
  isMulti?: boolean;
  isSelected?: boolean;
  leftIcon?: IconType;
  mode?: ActionMode;
  overrideContent?: React.ReactNode;
  search?: string;
  description?: string;
  descriptionTruncateMode?: 'center' | 'end';
  tags?: string[];
  tooltip?: string;
}

// can be extended to support other left content e.g. UserAvatar
const LeftContent = ({
  label,
  enableIndicator,
  isDanger = false,
  isIndeterminate,
  isMulti,
  isSelected,
  leftIcon,
  mode,
  overrideContent,
  search,
  description,
  descriptionTruncateMode,
  tooltip,
  tags = [],
}: LeftContentProps) => {
  const iconColor = isSelected ? 'status.active' : undefined;

  let indicatorIcon: Nullable<IconType> = null;
  if (enableIndicator) {
    if (isMulti) {
      if (isIndeterminate) {
        indicatorIcon = 'checkbox-indeterminate';
      } else if (isSelected) {
        indicatorIcon = 'checkbox-checked';
      } else {
        indicatorIcon = 'checkbox-empty';
      }
    } else if (isSelected) {
      indicatorIcon = 'radio-checked';
    } else {
      indicatorIcon = 'radio-empty';
    }
  }

  const DescriptionTextComponent =
    descriptionTruncateMode === 'center' ? CenterTruncateText : TruncateText;

  const chips = tags.map((tag) => ({ text: tag }));
  let color: 'action.link' | 'status.danger' | 'text.primary' = 'text.primary';
  if (mode === 'link') {
    color = 'action.link';
  } else if (isDanger) {
    color = 'status.danger';
  }

  return (
    <Layout align="center" minW={0} spacing={iconSpacing}>
      <Layout
        align="flex-start"
        ml={iconMarginXOffset}
        spacing={iconSpacing}
        styles={{ flexShrink: 0 }}
      >
        {indicatorIcon && (
          <Icon
            color={iconColor}
            icon={indicatorIcon}
            mt={iconMarginTopOffset}
          />
        )}
        {overrideContent ||
          (leftIcon && (
            <Icon
              color={iconColor}
              icon={leftIcon}
              mt={iconMarginTopOffset}
              tooltip={tooltip}
            />
          ))}
      </Layout>
      <Layout direction="column" minW={0}>
        <Box>
          {label && (
            <TruncateText color={color}>
              <MarkedText search={search} text={label} />
            </TruncateText>
          )}
          {chips && <Chips chips={chips}></Chips>}
        </Box>
        {description && (
          <DescriptionTextComponent color="text.secondary" variant="tiny">
            <MarkedText search={search} text={description} />
          </DescriptionTextComponent>
        )}
      </Layout>
    </Layout>
  );
};

interface RightContentProps {
  chips?: SharedChipProps[];
  rightIcon?: IconType;
  tooltip?: string;
}

// can be extended to support other right content e.g. Chips
const RightContent = ({ chips, rightIcon, tooltip }: RightContentProps) => {
  if (chips) {
    return <Chips chips={chips} />;
  } else if (rightIcon) {
    return (
      <Icon
        icon={rightIcon}
        mr={iconMarginXOffset}
        mt={iconMarginTopOffset}
        tooltip={tooltip}
      />
    );
  } else {
    return null;
  }
};

const componentStyles = {
  base: {
    cursor: 'pointer',
    minHeight: 'height.m',
    paddingBottom: 2,
    paddingLeft: 4,
    paddingRight: 4,
    paddingTop: 2,
    width: '100%',
  },
  tooltipTrigger: {
    minWidth: '100%',
  },
};
